@if (isOpen) {
  <div class="fixed inset-0 z-[1000] overflow-y-auto" data-testid="compose-email-modal">
    <div class="flex min-h-screen items-start justify-center p-4 sm:items-center">
      <div class="fixed inset-0 bg-black/50 transition-opacity" (click)="onClose()"></div>

      <div class="relative z-10 w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-xl dark:bg-gray-900">
        <div class="flex items-start gap-4 p-6 pb-4">
          <div class="flex h-11 w-11 items-center justify-center rounded-full bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-400">
            <lucide-icon [name]="MailIcon" [size]="22"></lucide-icon>
          </div>
          <div class="flex-1">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Write an email</h3>
            <p class="mt-1 text-sm text-gray-600 dark:text-gray-300">
              Pick a template or write your own. It sends straight away.
            </p>
          </div>
          <button
            type="button"
            class="text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-200"
            (click)="onClose()"
            aria-label="Close write an email">
            <lucide-icon [name]="XIcon" [size]="18"></lucide-icon>
          </button>
        </div>

        <form [formGroup]="form" class="space-y-4 px-6 pb-2">
          <div class="grid gap-4 sm:grid-cols-2">
            <!-- To -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300" for="compose-to">To</label>

              @if (fixedContact) {
                <div
                  id="compose-to"
                  data-testid="compose-fixed-contact"
                  class="mt-1 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:bg-gray-800/60 dark:text-gray-200">
                  <span class="font-medium">{{ fixedContact.name }}</span>
                  <span class="text-gray-500 dark:text-gray-400"> · {{ fixedContact.email }}</span>
                </div>
              } @else if (hasChosenContact && selectedContact) {
                <div
                  id="compose-to"
                  data-testid="compose-picked-contact"
                  class="mt-1 flex items-center justify-between gap-2 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:bg-gray-800/60 dark:text-gray-200">
                  <span>
                    <span class="font-medium">{{ selectedContact.name }}</span>
                    <span class="text-gray-500 dark:text-gray-400"> · {{ selectedContact.email }}</span>
                  </span>
                  <button
                    type="button"
                    data-testid="compose-contact-change"
                    (click)="clearChosenContact()"
                    class="shrink-0 text-xs font-medium text-brand-600 hover:underline dark:text-brand-400">
                    Change
                  </button>
                </div>
              } @else {
                <input
                  id="compose-to"
                  type="search"
                  [formControl]="contactSearchControl"
                  placeholder="Search by name or email, or type a new email"
                  data-testid="compose-contact-search"
                  class="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white" />

                @if (contacts.length) {
                  <ul
                    data-testid="compose-contact-results"
                    class="mt-2 max-h-40 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200 dark:divide-gray-800 dark:border-gray-700">
                    @for (contact of contacts; track contact.id) {
                      <li>
                        <button
                          type="button"
                          data-testid="compose-contact-result"
                          (click)="selectContact(contact)"
                          class="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-white/5">
                          <span class="font-medium">{{ contact.name }}</span>
                          <span class="text-gray-500 dark:text-gray-400">
                            · {{ contact.email }} ({{ contact.venue?.name ?? 'No venue' }})
                          </span>
                        </button>
                      </li>
                    }
                  </ul>
                } @else if (!searchingContacts && !showCreateContact) {
                  <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">No contacts match that search.</p>
                }

                @if (showCreateContact) {
                  <div
                    data-testid="compose-create-contact"
                    class="mt-2 grid gap-2 rounded-lg border border-dashed border-gray-300 p-3 sm:grid-cols-2 dark:border-gray-700">
                    <p class="col-span-2 text-xs text-gray-500 dark:text-gray-400">
                      Nobody matches {{ contactSearchControl.value }}. Add their name to create them and send.
                    </p>
                    <input
                      type="text"
                      [formControl]="newContactForm.controls.firstName"
                      placeholder="First name"
                      data-testid="compose-new-contact-first-name"
                      class="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
                    <input
                      type="text"
                      [formControl]="newContactForm.controls.lastName"
                      placeholder="Last name"
                      data-testid="compose-new-contact-last-name"
                      class="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
                  </div>
                }
              }
            </div>

            <!-- Template -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300" for="compose-template">
                Template
              </label>
              <select
                id="compose-template"
                data-testid="compose-template"
                (change)="onTemplateChange($any($event.target).value)"
                class="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white">
                <option value="">Write my own</option>
                @for (template of templates; track template.key) {
                  <option [value]="template.key">{{ template.name }}</option>
                }
              </select>
            </div>
          </div>

          @if (recipientHasRefusedEmail) {
            <div
              data-testid="compose-no-permission"
              class="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
              This contact has asked not to be emailed, so nothing can be sent to them.
            </div>
          }

          <!-- Subject -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300" for="compose-subject">
              Subject
            </label>
            <input
              id="compose-subject"
              type="text"
              formControlName="subject"
              data-testid="compose-subject"
              class="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
          </div>

          <!-- Message -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300" for="compose-body">Message</label>
            <textarea
              id="compose-body"
              rows="10"
              formControlName="body"
              data-testid="compose-body"
              class="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"></textarea>
            @if (placeholders.length) {
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Words in curly brackets, such as {{ placeholders.join(', ') }}, are filled in for the contact when it is
                sent. It goes out from the contact's venue.
              </p>
            }
          </div>

          <!-- Preview -->
          @if (preview) {
            <div
              data-testid="compose-preview"
              class="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/60">
              <p class="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Preview — to {{ preview.recipient_email }}
              </p>
              <p class="mt-1 text-sm font-semibold text-gray-800 dark:text-white/90">{{ preview.subject }}</p>
              <p class="mt-2 whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">{{ preview.body }}</p>
            </div>
          }

          @if (sendError) {
            <div
              data-testid="compose-error"
              class="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
              {{ sendError }}
            </div>
          }
        </form>

        <div class="mt-4 flex items-center justify-between gap-3 bg-gray-50 px-6 py-4 dark:bg-gray-800/60">
          <app-button
            [label]="previewing ? 'Previewing…' : 'Preview'"
            [style]="'secondary'"
            [icon]="EyeIcon"
            [disabled]="previewing || submitting || form.controls.contactId.value == null"
            data-testid="compose-preview-button"
            (click)="onPreview()">
          </app-button>

          <div class="flex items-center gap-3">
            <app-button
              [label]="'Cancel'"
              [style]="'secondary'"
              [icon]="XIcon"
              [disabled]="submitting"
              data-testid="compose-cancel"
              (click)="onClose()">
            </app-button>
            <app-button
              [label]="submitting ? 'Sending…' : 'Send'"
              [style]="'primary'"
              [icon]="SendIcon"
              [disabled]="!canSend"
              data-testid="compose-send"
              (click)="onSend()">
            </app-button>
          </div>
        </div>
      </div>
    </div>
  </div>
}
